import { formatearMoneda, hora12, type ArqueoReporteDetalle } from '@barbones/shared';
import { useQuery } from '@tanstack/react-query';
import { useId, type ReactNode } from 'react';
import { api } from '@/api/cliente';
import { Insignia } from '@/components/ui/data';
import { cn, fechaHora } from '@/lib/utils';

const METODO = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta' } as const;
const MOVIMIENTO: Record<string, string> = {
  ingreso: 'Ingreso',
  retiro: 'Retiro',
  gasto: 'Gasto',
  reembolso: 'Reembolso',
  ajuste: 'Ajuste',
  correccion_cobro: 'Corrección de cobro',
};

/** Cierre o corte con sus ventas. Consulta solo con un id; quien llama revisa `reportes.dueno`. */
export const useArqueoDetalle = (arqueoId: string | null | undefined) =>
  useQuery({ queryKey: ['reportes', 'arqueo', arqueoId], queryFn: () => api<ArqueoReporteDetalle>(`/reportes/caja/arqueos/${arqueoId}`), enabled: !!arqueoId });

function Seccion({ titulo, children }: { titulo: string; children: ReactNode }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="flex flex-col gap-1.5">
      <h3 id={id} className="text-[12px] font-medium uppercase tracking-wide text-foreground-muted">
        {titulo}
      </h3>
      {children}
    </section>
  );
}

function Dato({ etiqueta, valor, className }: { etiqueta: string; valor: ReactNode; className?: string }) {
  return (
    <div>
      <dt className="text-[12px] text-foreground-muted">{etiqueta}</dt>
      <dd className={cn('cifras text-[14px] font-medium', className)}>{valor}</dd>
    </div>
  );
}

function cuadre(a: ArqueoReporteDetalle): { texto: string; cuadra: boolean } {
  if (a.diferenciaMXN < 0) return { texto: `Faltante ${formatearMoneda(-a.diferenciaMXN)}`, cuadra: false };
  if (a.diferenciaMXN > 0) return { texto: `Excedente ${formatearMoneda(a.diferenciaMXN)}`, cuadra: false };
  if (a.diferenciaUSD) return { texto: `Diferencia ${formatearMoneda(a.diferenciaUSD, 'USD')}`, cuadra: false };
  return { texto: 'Cuadra', cuadra: true };
}

/** Detalle de solo lectura de un cierre o corte: el conteo, los totales y cada venta cobrada en el turno. */
export function DetalleArqueo({ arqueo }: { arqueo: ArqueoReporteDetalle }) {
  const c = cuadre(arqueo);
  const tono = c.cuadra ? 'text-success' : 'text-destructive';
  const t = arqueo.totales;
  return (
    <section aria-label={`${arqueo.tipo === 'cierre' ? 'Cierre' : 'Corte'} de caja del ${fechaHora(arqueo.fecha)}`} className="flex flex-col gap-4 text-[13px]">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[15px] font-medium">{arqueo.tipo === 'cierre' ? 'Cierre' : 'Corte'} de {arqueo.realizadoPor}</p>
          <Insignia tono={c.cuadra ? 'exito' : 'peligro'}>{c.texto}</Insignia>
        </div>
        <p className="flex flex-wrap justify-between gap-x-3 text-foreground-muted">
          <span>Turno de {arqueo.turno.responsable}, abierto {fechaHora(arqueo.turno.abiertoEn)}</span>
          <span className="cifras">{fechaHora(arqueo.fecha)}</span>
        </p>
      </div>

      <Seccion titulo="Efectivo">
        <dl className="grid grid-cols-2 gap-2 rounded-md border border-border px-3 py-2.5 sm:grid-cols-3">
          <Dato etiqueta="Esperado" valor={formatearMoneda(arqueo.esperadoMXN)} />
          <Dato etiqueta="Contado" valor={formatearMoneda(arqueo.contadoMXN)} className={tono} />
          <Dato etiqueta="Diferencia" valor={formatearMoneda(arqueo.diferenciaMXN)} className={tono} />
          {(arqueo.esperadoUSD > 0 || arqueo.contadoUSD > 0) && (
            <>
              <Dato etiqueta="Esperado USD" valor={formatearMoneda(arqueo.esperadoUSD, 'USD')} />
              <Dato etiqueta="Contado USD" valor={formatearMoneda(arqueo.contadoUSD, 'USD')} className={tono} />
              <Dato etiqueta="Diferencia USD" valor={formatearMoneda(arqueo.diferenciaUSD, 'USD')} className={tono} />
            </>
          )}
        </dl>
      </Seccion>

      <Seccion titulo={`Cobrado en el turno · ${arqueo.ventas.length} ${arqueo.ventas.length === 1 ? 'venta' : 'ventas'}`}>
        <dl className="cifras grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 px-1">
          <dt className="text-foreground-muted">Fondo inicial</dt>
          <dd className="text-right">{formatearMoneda(arqueo.fondoInicialMXN)}</dd>
          <dt className="text-foreground-muted">Efectivo (ya sin el cambio)</dt>
          <dd className="text-right">{formatearMoneda(t.efectivoMXN)}</dd>
          <dt className="text-foreground-muted">Transferencia</dt>
          <dd className="text-right">{formatearMoneda(t.transferenciaMXN)}</dd>
          <dt className="text-foreground-muted">Tarjeta</dt>
          <dd className="text-right">{formatearMoneda(t.tarjetaMXN)}</dd>
          <dt className="text-foreground-muted">Cambio entregado</dt>
          <dd className="text-right">{formatearMoneda(t.cambioMXN)}</dd>
          <dt className="text-[14px] font-semibold">Total vendido</dt>
          <dd className="text-right text-[14px] font-semibold">{formatearMoneda(t.ventasMXN)}</dd>
        </dl>
      </Seccion>

      <Seccion titulo="Ventas">
        {arqueo.ventas.length === 0 ? (
          <p className="text-foreground-muted">No hubo ventas cobradas.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {arqueo.ventas.map((v) => (
              <li key={v.ventaId} aria-label={`Folio ${v.folio}`} className="flex flex-col gap-2 rounded-md border border-border px-3 py-2.5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <p className="font-medium">
                    Folio {v.folio} · {v.cliente}
                  </p>
                  <span className="cifras text-foreground-muted">{hora12(new Date(v.pagadaEn))}</span>
                </div>
                <ul className="flex flex-col gap-0.5">
                  {v.conceptos.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span>
                        {l.cantidad > 1 ? `${l.cantidad}× ${l.descripcion}` : l.descripcion}
                        <span className="text-[12px] text-foreground-muted"> · {l.tipo === 'servicio' ? 'Servicio' : 'Producto'}</span>
                      </span>
                      <span className="cifras shrink-0">{formatearMoneda(l.totalMXN)}</span>
                    </li>
                  ))}
                </ul>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 border-t border-border pt-2">
                  <div>
                    <dt className="text-[12px] text-foreground-muted">Barbero</dt>
                    <dd>{v.barberos.join(', ') || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-[12px] text-foreground-muted">Cobró</dt>
                    <dd>{v.cobro}</dd>
                  </div>
                  <div>
                    <dt className="text-[12px] text-foreground-muted">Total a pagar</dt>
                    <dd className="cifras font-medium">{formatearMoneda(v.totalMXN)}</dd>
                  </div>
                  <div>
                    <dt className="text-[12px] text-foreground-muted">Pagó</dt>
                    <dd className="cifras">
                      {v.pagos.length === 0 && '—'}
                      {v.pagos.map((p, i) => (
                        <span key={i} className="block">
                          {formatearMoneda(p.monto, p.moneda)} {METODO[p.metodo].toLowerCase()}
                        </span>
                      ))}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[12px] text-foreground-muted">Cambio</dt>
                    <dd className="cifras">{formatearMoneda(v.cambioMXN)}</dd>
                  </div>
                  {v.propinaMXN > 0 && (
                    <div>
                      <dt className="text-[12px] text-foreground-muted">Propina</dt>
                      <dd className="cifras">{formatearMoneda(v.propinaMXN)}</dd>
                    </div>
                  )}
                </dl>
              </li>
            ))}
          </ul>
        )}
      </Seccion>

      {arqueo.movimientos.length > 0 && (
        <Seccion titulo="Movimientos de caja">
          <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
            {arqueo.movimientos.map((m, i) => (
              <li key={i} className="flex items-start justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <p>{m.concepto}</p>
                  <p className="text-[12px] text-foreground-muted">
                    {MOVIMIENTO[m.tipo] ?? m.tipo} · {m.usuario} · {hora12(new Date(m.fecha))}
                  </p>
                </div>
                <span className={cn('cifras shrink-0', m.monto < 0 && 'text-foreground-muted')}>{formatearMoneda(m.monto, m.moneda)}</span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </section>
  );
}
